import React, { useState, useRef, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const CATEGORY_STYLE = {
  'témoignage': { bg: 'rgba(29,158,117,0.12)', color: '#1D9E75', border: 'rgba(29,158,117,0.3)' },
  'conseil':    { bg: 'rgba(83,74,183,0.12)',  color: '#534AB7', border: 'rgba(83,74,183,0.3)' },
  'actualité':  { bg: 'rgba(196,120,74,0.12)', color: '#C4784A', border: 'rgba(196,120,74,0.3)' },
};

function filterMessage(text) {
  const patterns = [
    /[\w.-]+@[\w.-]+\.\w+/gi,
    /(\+33|0)[1-9][\s.-]?(\d{2}[\s.-]?){4}/g,
    /\b\d{10}\b/g,
    /(https?:\/\/|www\.)\S+/gi,
    /whatsapp|telegram|signal|instagram|facebook|snapchat/gi,
  ];
  let filtered = text;
  let blocked = false;
  patterns.forEach(p => {
    if (p.test(filtered)) { blocked = true; filtered = filtered.replace(p, '***'); }
  });
  return { filtered, blocked };
}

function Avatar({ name, size = 36 }) {
  const initials = (name || 'V').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const colors = ['linear-gradient(135deg,#0F6E56,#1D9E75)', 'linear-gradient(135deg,#534AB7,#8B80E0)', 'linear-gradient(135deg,#C4784A,#E8A87C)', 'linear-gradient(135deg,#1A1A18,#3a3a38)'];
  const gradient = colors[initials.charCodeAt(0) % colors.length];
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.33, fontWeight: 600, color: '#fff', flexShrink: 0 }}>
      {initials}
    </div>
  );
}

export default function CommunityPage({ onNavigate }) {
  const [user, setUser] = useState(null);
  const [hostProfile, setHostProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('posts');
  const isMobile = window.innerWidth < 768;

  const [posts, setPosts] = useState([]);
  const [postReplies, setPostReplies] = useState({});
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [newPost, setNewPost] = useState(false);
  const [newPostForm, setNewPostForm] = useState({ title: '', text: '', category: 'témoignage' });
  const [expandedPost, setExpandedPost] = useState(null);
  const [replyText, setReplyText] = useState('');

  const [qa, setQa] = useState([]);
  const [qaAnswers, setQaAnswers] = useState({});
  const [newQuestion, setNewQuestion] = useState(false);
  const [newQuestionText, setNewQuestionText] = useState('');
  const [expandedQa, setExpandedQa] = useState(null);
  const [answerText, setAnswerText] = useState('');

  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [onlineCount, setOnlineCount] = useState(1);
  const [blockedWarning, setBlockedWarning] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session?.user) { setLoading(false); return; }
      setUser(session.user);
      const { data: profile } = await supabase
        .from('host_profiles').select('*').eq('id', session.user.id).single();
      if (profile?.status === 'active') setHostProfile(profile);
      setLoading(false);
    });
  }, []);

  const loadPosts = useCallback(async () => {
    const { data } = await supabase.from('community_posts')
      .select('*, author:author_id(full_name, email)')
      .order('created_at', { ascending: false }).limit(20);
    if (data) setPosts(data);
  }, []);

  const loadQa = useCallback(async () => {
    const { data } = await supabase.from('community_qa')
      .select('*, author:author_id(full_name, email)')
      .order('created_at', { ascending: false }).limit(20);
    if (data) setQa(data);
  }, []);

  const loadChat = useCallback(async () => {
    const { data } = await supabase.from('chat_messages')
      .select('*, author:author_id(full_name, email)')
      .order('created_at', { ascending: false }).limit(50);
    if (data) setChat(data.reverse());
  }, []);

  const loadLikes = useCallback(async (uid) => {
    const { data } = await supabase.from('community_likes').select('post_id').eq('user_id', uid);
    if (data) setLikedPosts(new Set(data.map(l => l.post_id)));
  }, []);

  useEffect(() => {
    if (!user || !hostProfile) return;
    loadPosts(); loadQa(); loadChat(); loadLikes(user.id);

    const chatSub = supabase.channel('chat_messages')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages' }, async payload => {
        const { data } = await supabase.from('chat_messages')
          .select('*, author:author_id(full_name, email)').eq('id', payload.new.id).single();
        if (data) setChat(prev => [...prev, data]);
      }).subscribe();

    const postSub = supabase.channel('community_posts')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'community_posts' }, () => loadPosts())
      .subscribe();

    const replySub = supabase.channel('community_replies')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'community_replies' }, payload => {
        const postId = payload.new.post_id;
        setPostReplies(prev => ({ ...prev, [postId]: [...(prev[postId] || []), payload.new] }));
      }).subscribe();

    const presenceSub = supabase.channel('online_hosts')
      .on('presence', { event: 'sync' }, () => {
        const state = presenceSub.presenceState();
        setOnlineCount(Object.keys(state).length || 1);
      }).subscribe(async status => {
        if (status === 'SUBSCRIBED') await presenceSub.track({ user_id: user.id });
      });

    return () => {
      supabase.removeChannel(chatSub);
      supabase.removeChannel(postSub);
      supabase.removeChannel(replySub);
      supabase.removeChannel(presenceSub);
    };
  }, [user, hostProfile, loadPosts, loadQa, loadChat, loadLikes]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat]);

  const loadPostReplies = async (postId) => {
    if (postReplies[postId]) return;
    const { data } = await supabase.from('community_replies')
      .select('*, author:author_id(full_name, email)').eq('post_id', postId).order('created_at');
    if (data) setPostReplies(prev => ({ ...prev, [postId]: data }));
  };

  const loadQaAnswers = async (qaId) => {
    if (qaAnswers[qaId]) return;
    const { data } = await supabase.from('community_answers')
      .select('*, author:author_id(full_name, email)').eq('qa_id', qaId).order('votes', { ascending: false });
    if (data) setQaAnswers(prev => ({ ...prev, [qaId]: data }));
  };

  const warn = () => { setBlockedWarning(true); setTimeout(() => setBlockedWarning(false), 4000); };

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const { filtered, blocked } = filterMessage(chatInput);
    if (blocked) { warn(); setChatInput(''); return; }
    const text = replyTo ? `@${replyTo.name} ${filtered}` : filtered;
    setChatInput('');
    setReplyTo(null);
    await supabase.from('chat_messages').insert({ author_id: user.id, text });
  };

  const toggleLike = async (postId) => {
    const liked = likedPosts.has(postId);
    if (liked) {
      await supabase.from('community_likes').delete().eq('post_id', postId).eq('user_id', user.id);
      await supabase.from('community_posts').update({ likes: (posts.find(p => p.id === postId)?.likes || 1) - 1 }).eq('id', postId);
      setLikedPosts(prev => { const s = new Set(prev); s.delete(postId); return s; });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes: (p.likes || 1) - 1 } : p));
    } else {
      await supabase.from('community_likes').insert({ post_id: postId, user_id: user.id });
      await supabase.from('community_posts').update({ likes: (posts.find(p => p.id === postId)?.likes || 0) + 1 }).eq('id', postId);
      setLikedPosts(prev => new Set([...prev, postId]));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes: (p.likes || 0) + 1 } : p));
    }
  };

  const addReply = async (postId) => {
    if (!replyText.trim()) return;
    const { filtered, blocked } = filterMessage(replyText);
    if (blocked) { warn(); return; }
    await supabase.from('community_replies').insert({ post_id: postId, author_id: user.id, text: filtered });
    setReplyText('');
    loadPostReplies(postId);
  };

  const addAnswer = async (qaId) => {
    if (!answerText.trim()) return;
    const { filtered, blocked } = filterMessage(answerText);
    if (blocked) { warn(); return; }
    await supabase.from('community_answers').insert({ qa_id: qaId, author_id: user.id, text: filtered });
    await supabase.from('community_qa').update({ answered: true }).eq('id', qaId);
    setAnswerText('');
    setQaAnswers(prev => ({ ...prev, [qaId]: undefined }));
    loadQaAnswers(qaId);
    setQa(prev => prev.map(q => q.id === qaId ? { ...q, answered: true } : q));
  };

  const submitPost = async () => {
    if (!newPostForm.title.trim() || !newPostForm.text.trim()) return;
    const { filtered: ft, blocked: b1 } = filterMessage(newPostForm.title);
    const { filtered: fx, blocked: b2 } = filterMessage(newPostForm.text);
    if (b1 || b2) { warn(); return; }
    await supabase.from('community_posts').insert({ author_id: user.id, category: newPostForm.category, title: ft, text: fx, likes: 0 });
    setNewPost(false);
    setNewPostForm({ title: '', text: '', category: 'témoignage' });
    loadPosts();
  };

  const submitQuestion = async () => {
    if (!newQuestionText.trim()) return;
    const { filtered, blocked } = filterMessage(newQuestionText);
    if (blocked) { warn(); return; }
    await supabase.from('community_qa').insert({ author_id: user.id, question: filtered, votes: 0, answered: false });
    setNewQuestion(false);
    setNewQuestionText('');
    loadQa();
  };

  const getName = (item) => item?.author?.full_name || item?.author?.email?.split('@')[0] || 'Hôte';
  const getTime = (item) => {
    if (!item?.created_at) return "À l'instant";
    const diff = (Date.now() - new Date(item.created_at)) / 1000;
    if (diff < 60) return "À l'instant";
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
    return `Il y a ${Math.floor(diff / 86400)}j`;
  };

  const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 13, fontFamily: 'var(--sans)', outline: 'none', background: 'var(--white)', color: 'var(--ink)', boxSizing: 'border-box' };
  const btnSecondary = { padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 20, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)', color: 'var(--ink-3)' };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 60 }}>
      <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>Chargement...</div>
    </div>
  );

  if (!user || !hostProfile) return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 1.5rem' }}>
      <div style={{ maxWidth: 440, width: '100%', textAlign: 'center' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #0F6E56, #1D9E75)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
        </div>
        <h2 style={{ fontFamily: 'var(--serif)', fontSize: 26, letterSpacing: '-0.5px', marginBottom: 12 }}>Communauté hôtes</h2>
        <p style={{ fontSize: 15, color: 'var(--ink-3)', lineHeight: 1.7, marginBottom: 8 }}>
          Cet espace est réservé aux hôtes Havenly vérifiés et validés.
        </p>
        <div style={{ background: '#EEEDFE', border: '1px solid #AFA9EC', borderRadius: 10, padding: '12px 16px', marginBottom: 28, fontSize: 13, color: '#534AB7', lineHeight: 1.6 }}>
          Votre compte doit être activé par l'équipe Havenly pour accéder à cet espace.
        </div>
        <button onClick={() => onNavigate('auth')} style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg, #0F6E56, #1D9E75)', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)', marginBottom: 10 }}>
          Se connecter →
        </button>
        <button onClick={() => onNavigate('home')} style={{ width: '100%', padding: '11px', background: 'transparent', color: 'var(--ink-3)', border: '1px solid var(--border)', borderRadius: 12, fontSize: 14, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
          Retour à l'accueil
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh', paddingTop: 60 }}>

      {blockedWarning && (
        <div style={{ position: 'fixed', top: 70, left: '50%', transform: 'translateX(-50%)', zIndex: 99999, background: '#A32D2D', color: '#fff', padding: '12px 20px', borderRadius: 12, fontSize: 13, fontWeight: 500, boxShadow: '0 4px 20px rgba(0,0,0,0.2)', whiteSpace: 'nowrap' }}>
          ⚠️ Message bloqué — coordonnées personnelles interdites.
        </div>
      )}

      {/* Header */}
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 680, margin: '0 auto', padding: isMobile ? '1rem 1rem 0' : '1.5rem 2rem 0' }}>
          <button onClick={() => onNavigate('home')} style={{ background: 'none', border: 'none', fontSize: 13, color: 'var(--ink-3)', cursor: 'pointer', padding: 0, marginBottom: 12 }}>← Retour</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
            <div style={{ width: isMobile ? 56 : 72, height: isMobile ? 56 : 72, borderRadius: '50%', background: 'linear-gradient(135deg, #0F6E56 0%, #1D9E75 50%, #9FE1CB 100%)', padding: 3, flexShrink: 0 }}>
              <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: 'var(--white)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg viewBox="0 0 210 52" style={{ height: 18, width: 'auto' }} xmlns="http://www.w3.org/2000/svg">
                  <text x="0" y="44" fontFamily="Georgia,serif" fontSize="52" fontWeight="400" fontStyle="italic" letterSpacing="-2" fill="#1D9E75">haven<tspan fill="#0F6E56" dx="-3">ly</tspan></text>
                </svg>
              </div>
            </div>
            <div>
              <h1 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 18 : 22, letterSpacing: '-0.5px', marginBottom: 4 }}>Communauté hôtes</h1>
              <div style={{ display: 'flex', gap: 16, fontSize: 13, color: 'var(--ink-3)' }}>
                <span><strong style={{ color: 'var(--ink)' }}>{posts.length}</strong> posts</span>
                <span><strong style={{ color: 'var(--ink)' }}>{qa.length}</strong> questions</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />
                  <strong style={{ color: 'var(--ink)' }}>{onlineCount}</strong> en ligne
                </span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', borderTop: '1px solid var(--border)' }}>
            {[
              { key: 'posts', label: 'Posts', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> },
              { key: 'chat', label: 'Chat live', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>, dot: true },
              { key: 'qa',   label: 'Q & R',    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{ flex: 1, padding: '12px 0', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, borderTop: tab === t.key ? '2px solid var(--ink)' : '2px solid transparent', color: tab === t.key ? 'var(--ink)' : 'var(--ink-3)', fontWeight: tab === t.key ? 500 : 400, marginTop: -1 }}>
                {t.icon}
                {!isMobile && <span>{t.label}</span>}
                {t.dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: isMobile ? '1rem' : '1.5rem 2rem' }}>

        {/* POSTS */}
        {tab === 'posts' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {!newPost ? (
              <div onClick={() => setNewPost(true)} style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 16, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                <Avatar name={hostProfile?.full_name} size={38} />
                <div style={{ flex: 1, padding: '8px 14px', background: 'var(--surface)', borderRadius: 20, fontSize: 14, color: 'var(--ink-4)' }}>
                  Partagez un conseil ou témoignage...
                </div>
              </div>
            ) : (
              <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
                <div style={{ padding: '16px 16px 0' }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    {['témoignage', 'conseil', 'actualité'].map(cat => (
                      <button key={cat} onClick={() => setNewPostForm(p => ({ ...p, category: cat }))} style={{ padding: '5px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--sans)', fontWeight: newPostForm.category === cat ? 500 : 400, background: newPostForm.category === cat ? CATEGORY_STYLE[cat].bg : 'transparent', color: newPostForm.category === cat ? CATEGORY_STYLE[cat].color : 'var(--ink-3)', border: `1px solid ${newPostForm.category === cat ? CATEGORY_STYLE[cat].border : 'var(--border)'}` }}>{cat}</button>
                    ))}
                  </div>
                  <input placeholder="Titre..." value={newPostForm.title} onChange={e => setNewPostForm(p => ({ ...p, title: e.target.value }))}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: 16, fontWeight: 500, fontFamily: 'var(--sans)', background: 'transparent', color: 'var(--ink)', marginBottom: 10, boxSizing: 'border-box' }} />
                  <textarea placeholder="Partagez votre expérience..." value={newPostForm.text} onChange={e => setNewPostForm(p => ({ ...p, text: e.target.value }))}
                    style={{ width: '100%', height: 90, resize: 'none', border: 'none', outline: 'none', fontSize: 14, lineHeight: 1.7, fontFamily: 'var(--sans)', background: 'transparent', color: 'var(--ink)', boxSizing: 'border-box' }} />
                </div>
                <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>Coordonnées et liens interdits</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setNewPost(false)} style={btnSecondary}>Annuler</button>
                    <button onClick={submitPost} style={{ padding: '8px 18px', background: 'linear-gradient(135deg, #0F6E56, #1D9E75)', color: '#fff', border: 'none', borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>Publier</button>
                  </div>
                </div>
              </div>
            )}

            {posts.length === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem', background: 'var(--white)', borderRadius: 16, border: '1px solid var(--border)', color: 'var(--ink-3)', fontSize: 14 }}>
                Soyez le premier à publier un post !
              </div>
            )}

            {posts.map(post => (
              <div key={post.id} style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
                <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Avatar name={getName(post)} size={40} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{getName(post)}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{getTime(post)}</div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 20, background: CATEGORY_STYLE[post.category]?.bg, color: CATEGORY_STYLE[post.category]?.color, border: `1px solid ${CATEGORY_STYLE[post.category]?.border}` }}>
                    {post.category}
                  </span>
                </div>
                <div style={{ height: 4, background: post.category === 'témoignage' ? 'linear-gradient(90deg,#0F6E56,#1D9E75)' : post.category === 'conseil' ? 'linear-gradient(90deg,#534AB7,#8B80E0)' : 'linear-gradient(90deg,#C4784A,#E8A87C)' }} />
                <div style={{ padding: '14px 16px' }}>
                  <h3 style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>{post.title}</h3>
                  <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7, margin: 0 }}>{post.text}</p>
                </div>
                <div style={{ padding: '8px 16px 4px', display: 'flex', alignItems: 'center', gap: 16, borderTop: '1px solid var(--border)' }}>
                  <button onClick={() => toggleLike(post.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', fontFamily: 'var(--sans)' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill={likedPosts.has(post.id) ? '#E24B4A' : 'none'} stroke={likedPosts.has(post.id) ? '#E24B4A' : 'var(--ink-3)'} strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                    <span style={{ fontSize: 13, color: likedPosts.has(post.id) ? '#E24B4A' : 'var(--ink-3)' }}>{post.likes || 0}</span>
                  </button>
                  <button onClick={() => { const open = expandedPost === post.id; setExpandedPost(open ? null : post.id); if (!open) loadPostReplies(post.id); }} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', fontFamily: 'var(--sans)' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                    <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>{(postReplies[post.id] || []).length}</span>
                  </button>
                </div>
                {expandedPost === post.id && (
                  <div style={{ borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
                    {(postReplies[post.id] || []).map((reply, i) => (
                      <div key={i} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10 }}>
                        <Avatar name={reply?.author?.full_name || 'Hôte'} size={28} />
                        <div>
                          <span style={{ fontSize: 12, fontWeight: 500 }}>{reply?.author?.full_name || 'Hôte'} </span>
                          <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>· {getTime(reply)}</span>
                          <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6, marginTop: 2 }}>{reply.text}</div>
                        </div>
                      </div>
                    ))}
                    <div style={{ padding: '10px 16px', display: 'flex', gap: 8 }}>
                      <input value={replyText} onChange={e => setReplyText(e.target.value)} onKeyDown={e => e.key === 'Enter' && addReply(post.id)}
                        placeholder="Votre commentaire..." style={{ ...inputStyle, flex: 1, borderRadius: 20, fontSize: 13 }} />
                      <button onClick={() => addReply(post.id)} style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #0F6E56, #1D9E75)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>→</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* CHAT LIVE */}
        {tab === 'chat' && (
          <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: isMobile ? '68vh' : '72vh' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg, rgba(15,110,86,0.06), rgba(29,158,117,0.03))' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />
              <span style={{ fontSize: 13, fontWeight: 500 }}>Salon des hôtes</span>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>· {onlineCount} hôte{onlineCount > 1 ? 's' : ''} en ligne</span>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {chat.length === 0 && (
                <div style={{ textAlign: 'center', color: 'var(--ink-4)', fontSize: 13, marginTop: '2rem' }}>
                  Soyez le premier à écrire dans le salon !
                </div>
              )}
              {chat.map(msg => {
                const isMe = msg.author_id === user.id;
                const name = msg?.author?.full_name || msg?.author?.email?.split('@')[0] || 'Hôte';
                const time = new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                const isMention = msg.text.startsWith('@');
                const mentionName = isMention ? msg.text.split(' ')[0] : null;
                const mentionText = isMention ? msg.text.slice((mentionName?.length || 0) + 1) : msg.text;
                return (
                  <div key={msg.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexDirection: isMe ? 'row-reverse' : 'row' }}>
                    <Avatar name={name} size={34} />
                    <div style={{ maxWidth: '75%' }}>
                      {!isMe && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>{name} · {time}</div>}
                      <div style={{ fontSize: 14, lineHeight: 1.6, padding: '10px 14px', borderRadius: isMe ? '18px 4px 18px 18px' : '4px 18px 18px 18px', background: isMe ? 'linear-gradient(135deg, #0F6E56, #1D9E75)' : 'var(--surface)', color: isMe ? '#fff' : 'var(--ink)', border: isMe ? 'none' : '1px solid var(--border)' }}>
                        {isMention && (
                          <span style={{ color: isMe ? 'rgba(255,255,255,0.85)' : 'var(--green-mid)', fontWeight: 600, marginRight: 4 }}>{mentionName}</span>
                        )}
                        {isMention ? mentionText : msg.text}
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 4, justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                        {isMe && <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>{time}</div>}
                        {!isMe && (
                          <button onClick={() => setReplyTo({ name, id: msg.author_id })}
                            style={{ fontSize: 11, color: 'var(--ink-4)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'var(--sans)' }}>
                            ↩ Répondre
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>

            {/* Zone saisie avec reply */}
            <div style={{ borderTop: '1px solid var(--border)' }}>
              {replyTo && (
                <div style={{ padding: '6px 14px', background: 'var(--green-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--green-mid)' }}>
                    ↩ Répondre à <strong>{replyTo.name}</strong>
                  </span>
                  <button onClick={() => setReplyTo(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green-mid)', fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
                </div>
              )}
              <div style={{ padding: '10px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
                <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendChat()}
                  placeholder={replyTo ? `Répondre à ${replyTo.name}...` : 'Message...'}
                  style={{ ...inputStyle, flex: 1, borderRadius: 24, fontSize: 14 }} />
                <button onClick={sendChat} style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #0F6E56, #1D9E75)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Q&R */}
        {tab === 'qa' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {!newQuestion ? (
              <button onClick={() => setNewQuestion(true)} style={{ width: '100%', padding: '14px', background: 'var(--white)', border: '1px dashed #534AB7', borderRadius: 12, fontSize: 14, color: '#534AB7', fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#534AB7" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                Poser une question à la communauté
              </button>
            ) : (
              <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ height: 4, background: 'linear-gradient(90deg, #534AB7, #8B80E0)' }} />
                <div style={{ padding: '16px' }}>
                  <textarea placeholder="Posez votre question clairement..." value={newQuestionText} onChange={e => setNewQuestionText(e.target.value)}
                    style={{ ...inputStyle, height: 80, resize: 'none', lineHeight: 1.6, marginBottom: 10 }} />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={() => setNewQuestion(false)} style={btnSecondary}>Annuler</button>
                    <button onClick={submitQuestion} style={{ padding: '8px 18px', background: 'linear-gradient(135deg, #534AB7, #8B80E0)', color: '#fff', border: 'none', borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>Publier</button>
                  </div>
                </div>
              </div>
            )}

            {qa.length === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem', background: 'var(--white)', borderRadius: 16, border: '1px solid var(--border)', color: 'var(--ink-3)', fontSize: 14 }}>
                Posez la première question à la communauté !
              </div>
            )}

            {qa.map(q => (
              <div key={q.id} style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ height: 4, background: q.answered ? 'linear-gradient(90deg, #0F6E56, #1D9E75)' : 'linear-gradient(90deg, #534AB7, #8B80E0)' }} />
                <div style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flexShrink: 0, minWidth: 36 }}>
                      <div style={{ fontSize: 20, fontWeight: 600, color: '#534AB7' }}>{q.votes || 0}</div>
                      <div style={{ fontSize: 9, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: 1 }}>votes</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                        <Avatar name={getName(q)} size={24} />
                        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{getName(q)} · {getTime(q)}</span>
                        <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: q.answered ? 'var(--green-light)' : '#EEEDFE', color: q.answered ? 'var(--green-mid)' : '#534AB7' }}>
                          {q.answered ? '✓ Répondu' : 'Sans réponse'}
                        </span>
                      </div>
                      <p style={{ fontSize: 14, fontWeight: 500, margin: '0 0 10px', lineHeight: 1.5 }}>{q.question}</p>
                      <button onClick={() => { const open = expandedQa === q.id; setExpandedQa(open ? null : q.id); if (!open) loadQaAnswers(q.id); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#534AB7', fontFamily: 'var(--sans)', padding: 0 }}>
                        {(qaAnswers[q.id] || []).length} réponse{(qaAnswers[q.id] || []).length !== 1 ? 's' : ''} {expandedQa === q.id ? '▲' : '▼'}
                      </button>
                    </div>
                  </div>
                </div>
                {expandedQa === q.id && (
                  <div style={{ borderTop: '1px solid var(--border)' }}>
                    {(qaAnswers[q.id] || []).map((ans, i) => (
                      <div key={i} style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flexShrink: 0, minWidth: 30 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: ans.accepted ? 'var(--green-mid)' : '#534AB7' }}>{ans.votes || 0}</div>
                          {ans.accepted && <span style={{ fontSize: 14, color: 'var(--green-mid)' }}>✓</span>}
                        </div>
                        <div style={{ flex: 1, background: ans.accepted ? 'var(--green-light)' : 'var(--surface)', borderRadius: 12, padding: '12px 14px', border: ans.accepted ? '1px solid var(--green)' : '1px solid var(--border)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <Avatar name={ans?.author?.full_name || 'Hôte'} size={24} />
                            <span style={{ fontSize: 12, fontWeight: 500 }}>{ans?.author?.full_name || 'Hôte'}</span>
                            {ans.accepted && <span style={{ fontSize: 11, color: 'var(--green-mid)' }}>· Meilleure réponse</span>}
                          </div>
                          <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.65 }}>{ans.text}</div>
                        </div>
                      </div>
                    ))}
                    <div style={{ padding: '12px 16px', display: 'flex', gap: 8 }}>
                      <input value={answerText} onChange={e => setAnswerText(e.target.value)} onKeyDown={e => e.key === 'Enter' && addAnswer(q.id)}
                        placeholder="Votre réponse..." style={{ ...inputStyle, flex: 1, borderRadius: 20, fontSize: 13 }} />
                      <button onClick={() => addAnswer(q.id)} style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #534AB7, #8B80E0)', color: '#fff', border: 'none', cursor: 'pointer', flexShrink: 0, fontSize: 16 }}>→</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}